import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import Avatar from "@mui/material/Avatar";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import moment from "moment";
import { Localize } from "../../../app/lang/langs";
import { getNewsViewers } from "../../../containers/dashboard/dashboardSlice";
import { getCollabInitials } from "../../../app/utils/helpers";
import NewsViewsChart from "./NewsViewsChart";

/**
 * Who has read one post, and when.
 *
 * The list is loaded on open rather than held in the store: it is a snapshot of an
 * ever-growing table that nothing else in the app reads, and it must be current every time
 * it is opened. Closing it throws the rows away.
 *
 * Kept behind `news` EDIT at both call sites — read receipts name individuals, so only
 * someone who can publish sees them. The route enforces the same scope.
 */
export default function NewsViewersDialog({ newsId, title, createdAt, open, onClose }) {

    const dispatch = useDispatch()
    const [viewers, setViewers] = useState([])
    const [loading, setLoading] = useState(false)
    const [search, setSearch] = useState("")

    useEffect(() => {
        let cancelled = false
        setSearch("")
        if (!open || !newsId) {
            setViewers([])
            return
        }
        setLoading(true)
        dispatch(getNewsViewers({ newsId }))
            .unwrap()
            .then((response) => {
                if (cancelled) return
                const list = (response && response.responseData && response.responseData.viewers) || []
                // Most recent read first: the interesting end of the list is who just saw it.
                setViewers([...list].sort((a, b) => new Date(b.seen) - new Date(a.seen)))
            })
            .catch(() => { if (!cancelled) setViewers([]) })
            .finally(() => { if (!cancelled) setLoading(false) })
        return () => { cancelled = true }
    }, [open, newsId])

    // Case- and accent-insensitive, so "helene" finds "Hélène".
    const fold = (value) => (value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()

    const filteredViewers = useMemo(() => {
        const query = fold(search.trim())
        if (!query) return viewers
        return viewers.filter((viewer) => fold(viewer.collab_name).includes(query))
    }, [viewers, search])

    // "Prenom Nom" is what the backend concatenates; split it back for the avatar so the
    // initials match the ones CollabAvatar shows elsewhere.
    const initialsOf = (name) => {
        const parts = (name || "").trim().split(/\s+/)
        return getCollabInitials(parts[0], parts.slice(1).join(" "))
    }

    return (
        <Dialog
            open={Boolean(open)}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: { borderRadius: "16px" } }}
        >
            <DialogTitle sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontSize: "1rem",
                fontWeight: 600,
                background: "linear-gradient(135deg, rgba(255, 144, 21, 0.06) 0%, rgba(255, 193, 0, 0.04) 100%)",
                borderBottom: "1px solid rgba(255, 144, 21, 0.10)",
            }}>
                <VisibilityIcon sx={{ fontSize: 20, color: "#FF9015" }} />
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: "1rem", fontWeight: 600, lineHeight: 1.2 }}>
                        {Localize("NEWS_VIEWERS")}
                    </Typography>
                    {title && (
                        <Typography sx={{
                            fontSize: "0.75rem",
                            color: "#6b6560",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}>
                            {title}
                        </Typography>
                    )}
                </Box>
                {!loading && (
                    <Chip
                        size="small"
                        label={viewers.length}
                        sx={{
                            ml: "auto",
                            fontWeight: 600,
                            fontSize: "0.72rem",
                            color: "#B06000",
                            backgroundColor: "rgba(255, 144, 21, 0.12)",
                        }}
                    />
                )}
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0, maxHeight: 520 }}>
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress size={26} sx={{ color: "#FF9015" }} />
                    </Box>
                ) : viewers.length ? (
                    <>
                        {/* The chart is the shape of the readership; the list below it is
                            the same data row by row, so every timestamp stays reachable
                            without hovering anything. */}
                        <NewsViewsChart viewers={viewers} createdAt={createdAt} />
                        <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                            <TextField
                                size="small"
                                fullWidth
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder={Localize("SEARCH")}
                                inputProps={{ "aria-label": Localize("SEARCH") }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ fontSize: 18, color: "#6b6560" }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "10px",
                                        fontSize: "0.85rem",
                                        "&.Mui-focused fieldset": { borderColor: "#FF9015" },
                                    },
                                }}
                            />
                        </Box>
                        {!filteredViewers.length && (
                            <Box sx={{ py: 3, px: 3, textAlign: "center" }}>
                                <Typography sx={{ fontSize: "0.85rem", color: "#6b6560" }}>
                                    {Localize("NO_RESULTS")}
                                </Typography>
                            </Box>
                        )}
                        <List dense disablePadding>
                            {filteredViewers.map((viewer) => (
                                <ListItem key={viewer.id_collab} sx={{ px: 2 }}>
                                    <ListItemAvatar sx={{ minWidth: 44 }}>
                                        <Avatar sx={{
                                            width: 32,
                                            height: 32,
                                            fontSize: "0.75rem",
                                            fontWeight: 600,
                                            color: "#B06000",
                                            backgroundColor: "rgba(255, 144, 21, 0.14)",
                                        }}>
                                            {initialsOf(viewer.collab_name)}
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={viewer.collab_name}
                                        secondary={viewer.seen
                                            ? moment(viewer.seen).format("DD/MM/YYYY HH:mm")
                                            : null}
                                        primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 500 }}
                                        secondaryTypographyProps={{ fontSize: "0.72rem" }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </>
                ) : (
                    <Box sx={{ py: 4, px: 3, textAlign: "center" }}>
                        <Typography sx={{ fontSize: "0.85rem", color: "#6b6560" }}>
                            {Localize("NEWS_VIEWERS_EMPTY")}
                        </Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} sx={{ color: "#6b6560", textTransform: "none" }}>
                    {Localize("CLOSE")}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
